// Per DESIGN.md §3 — the verbatim copy below is the source-of-truth string.
// "Anonymous · nothing saved between visits · no sign-in" is the privacy line.
//
// Question content lives in `src/demoQuestions.ts` (single source of truth
// shared with Demo.tsx and AtlasShell's ⌘K palette). Edit there.

import { DEMO_QUESTIONS } from '../demoQuestions';
import type { DemoQuestion, IconName } from '../demoQuestions';

// Re-export so existing imports keep working.
export type SuggestedQuery = DemoQuestion;
export const SUGGESTED_QUERIES = DEMO_QUESTIONS;

interface EmptyStateProps {
  onSelectSuggestion: (query: string) => void;
  compact?: boolean;
}

export function EmptyState({ onSelectSuggestion, compact = false }: EmptyStateProps) {
  return (
    <div className={`empty-state ${compact ? 'empty-state--compact' : ''}`}>
      {!compact && <div className="empty-state-icon">✦</div>}
      <h1 className="empty-state-title">Ask anything about HRSA's data</h1>
      <p className="empty-state-tagline">
        One question. One answer. With map, chart, table, or list — and links to the original HRSA sources.
      </p>
      {!compact && (
        <p className="empty-state-privacy">
          🔒 Anonymous · nothing saved between visits · no sign-in
        </p>
      )}

      <div className="empty-state-section-label">Try one of these</div>
      <div className="empty-state-grid">
        {DEMO_QUESTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => onSelectSuggestion(s.query)}
            className="suggestion-card"
            title={s.purpose}
          >
            <div className="suggestion-card-head">
              <div className="suggestion-icon" aria-hidden="true">{iconChar(s.iconName)}</div>
              <div className="suggestion-short">{s.shortLabel}</div>
            </div>
            <div className="suggestion-label">{s.query}</div>
            <div className="suggestion-datasets" aria-label="Datasets used">
              {s.datasets.map((d) => (
                <span key={d} className="suggestion-dataset-chip">{d}</span>
              ))}
              {s.partialDataCoverage && (
                <span className="suggestion-dataset-chip suggestion-dataset-chip--partial"
                      title="One or more datasets aren't fully wired into the tool layer yet — the agent will answer with what it has and acknowledge the gap.">
                  partial coverage
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      {!compact && (
        <p className="empty-state-footer">Or type your own question below ↓</p>
      )}
    </div>
  );
}

function iconChar(name: IconName): string {
  return {
    info: 'ℹ',
    map: '🗺',
    table: '⊞',
    'chart-bar': '📊',
    'chart-pie': '◔',
    'chart-line': '📈',
    list: '☰',
    poly: '⬡',
  }[name];
}
